import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, EMPTY, filter, finalize, from, merge, switchMap } from 'rxjs';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Steps } from '../../../shared/components/steps/steps';
import { CardConteudo } from '../../../shared/components/card-conteudo/card-conteudo';
import { VivestControl } from '../../../core/directives/vivest-control';
import {
  Beneficio,
  BeneficioFormulario,
  LimitesBeneficio,
  ModalidadeRecebimento,
} from '../../../core/models/requerimento';
import { toBeneficioPayload } from '../../../core/models/requerimento-mapper';
import { LoaderService } from '../../../shared/services/loader-service';
import { RequerimentoService } from '../../../shared/services/requerimento-service';
import { RequerimentoState } from '../../../shared/services/requerimento-state';
import { ToastService } from '../../../shared/services/toast-service';
import { valorBrutoAntecipacao } from '../../../shared/utils/beneficio-resumo';
import {
  distribuirErrosDaApi,
  errosNosControles,
  mensagemDoServidor,
  ROTA_PAGINA_INDISPONIVEL,
  rotaDoErroRequerimento,
} from '../../../shared/utils/requerimento-erros';

export type FormaRecebimento = 'percentual-saldo' | 'prazo-certo' | 'valor-fixo-mensal';

interface OpcaoRecebimento {
  value: FormaRecebimento;
  titulo: string;
  descricao: string;
}

// TODO: substituir os dados mockados pela resposta da API quando ela existir.
const OPCOES_RECEBIMENTO: OpcaoRecebimento[] = [
  {
    value: 'valor-fixo-mensal',
    titulo: 'Valor Fixo Mensal',
    descricao: 'Lorem ipsum dolor sit amet, elit dames consectetur adipiscing elit.',
  },
  {
    value: 'percentual-saldo',
    titulo: 'Percentual de Saldo',
    descricao: 'Lorem ipsum dolor sit amet, elit dames consectetur adipiscing elit.',
  },
  {
    value: 'prazo-certo',
    titulo: 'Prazo Certo',
    descricao: 'Lorem ipsum dolor sit amet, elit dames consectetur adipiscing elit.',
  },
];

/**
 * O valor mensal equivalente fica 0 até a primeira simulação; com benefício já salvo no requerimento (GET
 * /v1/requerimentos/{id}), o preenchimento do formulário dispara o GET /simulacao com a última configuração.
 */
// TODO: a duração estimada não vem do GET /simulacao; substituir quando o backend devolver esse valor.
const SIMULACAO_MOCK = {
  duracaoEstimadaAnos: 12,
  valorMensalEquivalente: 0,
};

const formatoValor = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const PERCENTUAL_MAXIMO_ANTECIPACAO = 25;
const PERCENTUAL_MAXIMO_MENSAL = 10;
const PRAZO_MINIMO_ANOS = 5;
const PRAZO_MAXIMO_ANOS = 20;

/** Limites até o GET /saldos responder (ou se ele falhar); um mínimo ou máximo null da API também cai aqui. */
const LIMITES_PADRAO: LimitesBeneficio = {
  permiteAntecipacao: true,
  percentualMinimoAntecipacao: 0,
  percentualMaximoAntecipacao: PERCENTUAL_MAXIMO_ANTECIPACAO,
  elegivelAdiantamento: true,
  permiteValorFixoMensal: true,
  permitePercentualSaldo: true,
  percentualMinimoMensal: 0,
  percentualMaximoMensal: PERCENTUAL_MAXIMO_MENSAL,
  permitePrazoCerto: true,
  prazoMinimoAnos: PRAZO_MINIMO_ANOS,
  prazoMaximoAnos: PRAZO_MAXIMO_ANOS,
};

const FORMA_PERMITIDA: Record<FormaRecebimento, (limites: LimitesBeneficio) => boolean> = {
  'valor-fixo-mensal': (limites) => limites.permiteValorFixoMensal,
  'percentual-saldo': (limites) => limites.permitePercentualSaldo,
  'prazo-certo': (limites) => limites.permitePrazoCerto,
};

/** Campo do formulário usado por cada forma de recebimento; só o da forma selecionada é validado. */
const CAMPO_POR_FORMA = {
  'percentual-saldo': 'percentualMensal',
  'prazo-certo': 'prazoAnos',
  'valor-fixo-mensal': 'valorMensal',
} as const satisfies Record<FormaRecebimento, string>;

const MODALIDADE_POR_FORMA: Record<FormaRecebimento, ModalidadeRecebimento> = {
  'percentual-saldo': 'S',
  'prazo-certo': 'P',
  'valor-fixo-mensal': 'F',
};

const FORMA_POR_MODALIDADE: Record<ModalidadeRecebimento, FormaRecebimento> = {
  S: 'percentual-saldo',
  P: 'prazo-certo',
  F: 'valor-fixo-mensal',
};

type CampoBeneficio = 'percentualAntecipacao' | 'percentualMensal' | 'prazoAnos' | 'valorMensal';

const CAMPO_DA_API_BENEFICIO: Record<string, CampoBeneficio> = {
  pctAntecipSaldo: 'percentualAntecipacao',
  pctSaldoMensal: 'percentualMensal',
  qtdAnosPrazo: 'prazoAnos',
  vlFixoMensal: 'valorMensal',
};

/** Espera o participante parar de digitar antes de simular. */
export const ATRASO_SIMULACAO_MS = 500;

const MENSAGEM_ERRO_SALVAR = 'Não foi possível salvar a configuração. Tente novamente.';
const MENSAGEM_ERRO_SALDOS = 'Não foi possível carregar o saldo e os limites do plano. Tente novamente mais tarde.';

function paraCampoDecimal(valor: number | null): string {
  return valor === null ? '' : String(valor).replace('.', ',');
}

/** Converte o valor em reais digitado (R$ 6.087,87) para número. */
function paraNumero(valor: string): number {
  // Descarta "R$", espaços e pontos de milhar; a vírgula vira o separador decimal.
  return Number(valor.replace(/[^\d,]/g, '').replace(',', '.'));
}

/**
 * Valida um valor em reais digitado no formato brasileiro, a partir de 0. Sem teto: o GET /saldos não traz um valor
 * máximo mensal.
 */
const valorEmReaisValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) return null;

  const valor = paraNumero(control.value);
  return Number.isNaN(valor) || valor < 0 ? { valorEmReais: true } : null;
};

@Component({
  imports: [Steps, CardConteudo, ReactiveFormsModule, VivestControl],
  selector: 'app-configuracao-beneficio',
  styleUrl: './configuracao-beneficio.scss',
  templateUrl: './configuracao-beneficio.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ConfiguracaoBeneficio implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly loaderService = inject(LoaderService);
  private readonly requerimentoService = inject(RequerimentoService);
  private readonly requerimentoState = inject(RequerimentoState);
  private readonly toastService = inject(ToastService);

  readonly stepAtual = signal(2);
  readonly totalSteps = signal(7);
  readonly formaRecebimento = signal<FormaRecebimento>('valor-fixo-mensal');
  readonly adiantamentoBeneficio = signal(true);
  /** Saldo geral da conta (participante + patrocinadora), do GET /saldos. */
  protected readonly saldoGeral = signal(0);
  /** Limites do plano (GET /saldos): faixas dos campos e quais opções a tela oferece. */
  protected readonly limites = signal(LIMITES_PADRAO);

  protected readonly opcoesRecebimento = computed(() =>
    OPCOES_RECEBIMENTO.filter((opcao) => FORMA_PERMITIDA[opcao.value](this.limites())),
  );
  /** A forma selecionada só tem pergunta e campo se o plano a oferece (opcoes_renda do GET /saldos). */
  protected readonly formaPermitida = computed(() => FORMA_PERMITIDA[this.formaRecebimento()](this.limites()));
  protected readonly simulacao = signal(SIMULACAO_MOCK);
  protected readonly percentualMinimoAntecipacao = computed(
    () => this.limites().percentualMinimoAntecipacao ?? 0,
  );
  protected readonly percentualMaximoAntecipacao = computed(
    () => this.limites().percentualMaximoAntecipacao ?? PERCENTUAL_MAXIMO_ANTECIPACAO,
  );
  protected readonly percentualMinimoMensal = computed(() => this.limites().percentualMinimoMensal ?? 0);
  protected readonly percentualMaximoMensal = computed(
    () => this.limites().percentualMaximoMensal ?? PERCENTUAL_MAXIMO_MENSAL,
  );
  protected readonly prazoMinimoAnos = computed(() => this.limites().prazoMinimoAnos ?? PRAZO_MINIMO_ANOS);
  protected readonly prazoMaximoAnos = computed(() => this.limites().prazoMaximoAnos ?? PRAZO_MAXIMO_ANOS);

  protected readonly form = this.formBuilder.nonNullable.group({
    // Antecipação é opcional; só é validado o limite quando informado.
    percentualAntecipacao: ['', [Validators.min(0), Validators.max(PERCENTUAL_MAXIMO_ANTECIPACAO)]],
    percentualMensal: [
      '',
      [Validators.required, Validators.min(0), Validators.max(PERCENTUAL_MAXIMO_MENSAL)],
    ],
    prazoAnos: [
      '',
      [Validators.required, Validators.min(PRAZO_MINIMO_ANOS), Validators.max(PRAZO_MAXIMO_ANOS)],
    ],
    valorMensal: ['', [Validators.required, valorEmReaisValidator]],
  });

  private readonly statusFormulario = toSignal(this.form.statusChanges, {
    initialValue: this.form.status,
  });

  /** Libera o continuar com o campo da forma de recebimento preenchido e os valores dentro dos limites. */
  protected readonly podeContinuar = computed(() => this.statusFormulario() === 'VALID');

  private readonly percentualAntecipacaoDigitado = toSignal(this.form.controls.percentualAntecipacao.valueChanges, {
    initialValue: this.form.controls.percentualAntecipacao.value,
  });

  /** Valor bruto da antecipação: o percentual digitado sobre o saldo geral. */
  protected readonly valorAntecipacao = computed(() =>
    valorBrutoAntecipacao(Number(this.percentualAntecipacaoDigitado().replace(',', '.')) || 0, this.saldoGeral()),
  );

  constructor() {
    // Desabilitado, o campo é ignorado na validação; assim só a forma selecionada é obrigatória, e só se o plano a
    // oferece (fl_vlr_fixo_mensal, fl_pct_saldo, fl_prazo_certo do GET /saldos).
    effect(() => {
      const campoAtivo = this.formaPermitida() ? CAMPO_POR_FORMA[this.formaRecebimento()] : null;

      Object.values(CAMPO_POR_FORMA).forEach((campo) => {
        const control = this.form.controls[campo];

        if (campo === campoAtivo) {
          control.enable();
        } else {
          control.disable();
        }
      });
    });

    // GET /simulacao a cada mudança da forma, dos valores ou do adiantamento, só com a tela válida. O switchMap descarta
    // a resposta de uma simulação que ficou para trás; falhando, o valor anterior continua na tela.
    merge(this.form.valueChanges, toObservable(this.formaRecebimento), toObservable(this.adiantamentoBeneficio))
      .pipe(
        debounceTime(ATRASO_SIMULACAO_MS),
        filter(() => this.form.valid),
        switchMap(() => {
          const requerimento = this.requerimentoState.requerimento();
          if (!requerimento) return EMPTY;
          // O finalize também roda quando o switchMap descarta a simulação, mantendo o loader balanceado.
          this.loaderService.showLoader('Simulando...');
          return from(this.requerimentoService.simularBeneficio(requerimento.id, this.montarFormulario())).pipe(
            catchError(() => EMPTY),
            finalize(() => this.loaderService.hideLoader()),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe(({ valorBruto }) =>
        this.simulacao.update((simulacao) => ({ ...simulacao, valorMensalEquivalente: valorBruto })),
      );
  }

  async ngOnInit(): Promise<void> {
    this.loaderService.showLoader('Carregando...');
    try {
      const requerimento = await this.requerimentoState.carregar();
      if (requerimento.beneficio) {
        this.preencherFormulario(requerimento.beneficio);
      }
      await this.carregarSaldos(requerimento.id);
    } catch (erro) {
      this.router.navigate([rotaDoErroRequerimento(erro) ?? ROTA_PAGINA_INDISPONIVEL]);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  /** GET /saldos: o saldo geral e os limites do plano. Falhando, a tela segue com os limites padrão. */
  private async carregarSaldos(id: number): Promise<void> {
    try {
      const { saldoTotal, limites } = await this.requerimentoService.getSaldos(id);
      this.saldoGeral.set(saldoTotal);
      this.aplicarLimites(limites);
    } catch {
      this.toastService.show(MENSAGEM_ERRO_SALDOS, 'error');
    }
  }

  /**
   * Ajusta a tela ao plano: faixas dos campos, antecipação e adiantamento só quando permitidos e, se a forma salva não
   * for oferecida pelo plano, a primeira que for.
   */
  private aplicarLimites(limites: LimitesBeneficio): void {
    this.limites.set(limites);
    const { percentualAntecipacao, percentualMensal, prazoAnos } = this.form.controls;

    percentualAntecipacao.setValidators([
      Validators.min(this.percentualMinimoAntecipacao()),
      Validators.max(this.percentualMaximoAntecipacao()),
    ]);
    percentualMensal.setValidators([
      Validators.required,
      Validators.min(this.percentualMinimoMensal()),
      Validators.max(this.percentualMaximoMensal()),
    ]);
    prazoAnos.setValidators([
      Validators.required,
      Validators.min(this.prazoMinimoAnos()),
      Validators.max(this.prazoMaximoAnos()),
    ]);

    if (!limites.permiteAntecipacao) {
      percentualAntecipacao.setValue('');
      percentualAntecipacao.disable();
    }
    [percentualAntecipacao, percentualMensal, prazoAnos].forEach((control) => control.updateValueAndValidity());

    const formas = this.opcoesRecebimento().map((opcao) => opcao.value);
    if (formas.length > 0 && !formas.includes(this.formaRecebimento())) {
      this.formaRecebimento.set(formas[0]);
    }
    if (!limites.elegivelAdiantamento) {
      this.adiantamentoBeneficio.set(false);
    }
  }

  protected formatarValor(valor: number): string {
    return formatoValor.format(valor);
  }

  protected status(campo: keyof typeof this.form.controls): 'default' | 'error' {
    const control = this.form.controls[campo];
    return control.invalid && control.touched ? 'error' : 'default';
  }

  protected mensagemErro(campo: CampoBeneficio): string {
    return mensagemDoServidor(this.form.controls[campo]);
  }

  /** O vivest-checkbox dispara `change` e expõe o estado na propriedade `checked`. */
  protected onAdiantamentoChange(event: Event): void {
    this.adiantamentoBeneficio.set((event.target as HTMLElement & { checked: boolean }).checked);
  }

  onReturn() {
    this.router.navigate(['/requerimento-beneficio/dados-cadastrais']);
  }

  async onSubmit() {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    const requerimento = this.requerimentoState.requerimento();
    if (!requerimento) {
      this.router.navigate(['/requerimento-beneficio']);
      return;
    }

    this.loaderService.showLoader('Salvando...');
    try {
      const atualizado = await this.requerimentoService.salvar(requerimento.id, {
        beneficio: toBeneficioPayload(this.montarFormulario()),
      });
      this.requerimentoState.definir(atualizado);
      this.router.navigate(['/requerimento-beneficio/modalidade-imposto']);
    } catch (erro) {
      this.tratarErroAoSalvar(erro);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  private preencherFormulario(beneficio: Beneficio): void {
    if (beneficio.modalidade) {
      this.formaRecebimento.set(FORMA_POR_MODALIDADE[beneficio.modalidade]);
    }

    this.form.patchValue({
      percentualAntecipacao: paraCampoDecimal(beneficio.percentualAntecipacaoSaldo),
      percentualMensal: paraCampoDecimal(beneficio.percentualSaldoMensal),
      prazoAnos: paraCampoDecimal(beneficio.qtdAnosPrazo),
      valorMensal:
        beneficio.valorFixoMensal === null ? '' : this.formatarValor(beneficio.valorFixoMensal),
    });
    this.adiantamentoBeneficio.set(beneficio.adiantaBeneficio);
  }

  private montarFormulario(): BeneficioFormulario {
    const valores = this.form.getRawValue();

    return {
      modalidade: MODALIDADE_POR_FORMA[this.formaRecebimento()],
      percentualAntecipacao: valores.percentualAntecipacao,
      percentualMensal: valores.percentualMensal,
      prazoAnos: valores.prazoAnos,
      valorMensal: valores.valorMensal,
      adiantaBeneficio: this.adiantamentoBeneficio(),
    };
  }

  private tratarErroAoSalvar(erro: unknown): void {
    const rota = rotaDoErroRequerimento(erro);
    if (rota) {
      this.router.navigate([rota]);
      return;
    }

    const destinos = errosNosControles(this.form.controls, CAMPO_DA_API_BENEFICIO);
    distribuirErrosDaApi(erro, destinos, MENSAGEM_ERRO_SALVAR).forEach((mensagem) =>
      this.toastService.show(mensagem, 'error'),
    );
  }
}
